<template>
  <sign-in-content
    class="login-form"
  >
    <template v-if="!saml">
      <sso-buttons
        class="_sso"
        :loading="ssoLoading"
        @google="goToGoogle"
        @microsoft="goToMicrosoft"
        @saml="saml = true"
      />
      <div class="_errors">
        <o-auth-error
          v-if="emailNotRegistered[oAuthProvider.google] !== ''"
          :email="emailNotRegistered[oAuthProvider.google]"
          :provider="oAuthProvider.google"
        />
        <o-auth-error
          v-if="emailNotRegistered[oAuthProvider.microsoft] !== ''"
          :email="emailNotRegistered[oAuthProvider.microsoft]"
          :provider="oAuthProvider.microsoft"
        />
      </div>
      <m-divider small />
      <password-login
        :loading="passwordLoading"
        @submit="submit"
      />
    </template>
    <template v-else>
      <saml-login
        v-if="samlChoice === null"
        popup
        @session-established="$emit('session-established')"
        @choose="samlChoice = $event"
      />
      <saml-account-chooser
        v-else
        :candidates="samlChoice.candidates"
        :email="samlChoice.email"
        :loading-slug="chooserLoadingSlug"
        @select="chooseSamlAccount"
      />
      <div class="_saml-back">
        <m-link
          inherit-color
          @click="disableSaml"
        >
          {{ $t('login.back') }}
        </m-link>
      </div>
    </template>
  </sign-in-content>
</template>

<script>
import OAuthError from '@/components/OAuthError.vue';
import PasswordLogin from '@/components/PasswordLogin.vue';
import SamlAccountChooser from '@/components/SamlAccountChooser.vue';
import SamlLogin from '@/components/SamlLogin.vue';
import SignInContent from '@/components/SignInContent.vue';
import SsoButtons from '@/components/SsoButtons.vue';
import { authMethod, oAuthProvider } from 'shared/constants.json';
import { doLoginUser } from '@/api/sentinel';
import { encodeURLValues } from '@/lib/url';
import { errorType, samlLoginStatus } from '@/constants/sentinel.json';
import { initOAuthLogin } from '@/lib/oauth-redirect';
import { initSamlLogin } from '@/lib/saml';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'LoginForm',
  components: { SamlAccountChooser, SamlLogin, SignInContent, OAuthError, PasswordLogin, SsoButtons },
  data() {
    return {
      show: false,
      ssoLoading: null,
      password: '',
      email: '',
      emailNotRegistered: oAuthProvider.all.reduce((res, cur) => ({ ...res, [cur]: '' }), {}),
      oAuthProvider,
      passwordLoading: false,
      saml: false,
      samlChoice: null,
      chooserLoadingSlug: null,
    };
  },
  emits: ['session-established'],
  computed: { ...mapState({ loginURLs: (state) => state.loginURLs }) },
  methods: {
    ...mapActions([
      'oauthLogin',
    ]),
    goToGoogle() {
      const params = {
        action: 'login',
        provider: oAuthProvider.google,
      };
      const url = `/#/oauth-redirect?${encodeURLValues(params)}`;

      this.ssoLoading = authMethod.ssoGoogle;
      initOAuthLogin(url).then(this.subscribeWindowEvents)
        .finally(() => {
          this.ssoLoading = null;
        });
    },
    goToMicrosoft() {
      const params = {
        action: 'login',
        provider: oAuthProvider.microsoft,
      };
      const url = `/#/oauth-redirect?${encodeURLValues(params)}`;

      this.ssoLoading = authMethod.ssoMicrosoft;
      initOAuthLogin(url).then(this.subscribeWindowEvents)
        .finally(() => {
          this.ssoLoading = null;
        });
    },
    disableSaml() {
      this.saml = false;
      this.samlChoice = null;
      this.chooserLoadingSlug = null;
      this.$route.query.saml_login_status = undefined;
    },
    chooseSamlAccount(slug) {
      this.chooserLoadingSlug = slug;
      initSamlLogin({ email: this.samlChoice.email, slug })
        .then((event) => {
          const { error } = event.data;
          if (error !== '') {
            this.$showSnackbar({ color: 'error', message: this.samlErrorMessage(error) });
            return;
          }
          this.$emit('session-established');
        })
        .catch(() => {})
        .finally(() => {
          this.chooserLoadingSlug = null;
        });
    },
    samlErrorMessage(status) {
      if (samlLoginStatus.all.includes(status)) {
        return this.$t(`login.samlLoginStatus.${status}`);
      }
      return this.$t('error.default');
    },
    submit({ email, password }) {
      this.passwordLoading = true;
      doLoginUser({
        email,
        password,
      })
        .then((response) => {
          this.passwordLoading = false;
          if (response.status === 400) {
            this.$showSnackbar({ color: 'error', message: this.$t('login.emailPasswordCombinationWrong') });
            return;
          }
          if (response.status === 403) {
            this.$showSnackbar({ color: 'error', message: this.$t('login.passwordLoginRestricted') });
            return;
          }
          if (response.status !== 200) {
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            return;
          }

          this.$emit('session-established');
        });
    },
    subscribeWindowEvents(event) {
      const { provider, error, email } = event.data;
      if (error !== '') {
        switch (error) {
          case errorType.noSuchUser:
            this.emailNotRegistered[provider] = email;
            break;
          case errorType.googleLoginNotAllowed:
            this.$showSnackbar({ color: 'error', message: this.$t('loginForm.googleLoginNotAllowed') });
            break;
          case errorType.microsoftLoginNotAllowed:
            this.$showSnackbar({ color: 'error', message: this.$t('loginForm.microsoftLoginNotAllowed') });
            break;
          default:
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        }
        return Promise.resolve();
      }

      this.$emit('session-established');
      return Promise.resolve();
    },
  },
  mounted() {
    if (this.$route.query.email !== undefined) {
      this.email = this.$route.query.email;
    }

    if (this.$route.query.saml_login_status !== undefined) {
      this.saml = true;
    }
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .login-form {
    width: 100%;

    ._errors {
      color: map_get($orange, 'base');

      > * {
        padding: 0 .8rem;
        margin-top: .8rem;
      }
    }

    ._saml-back {
      margin-top: 2rem;
      font-size: $font-size-3;
      color: $font-color-secondary;
      text-align: center;
      text-decoration: underline;
    }
  }
</style>
